import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {
  premiumPlacementsAdminService,
  PremiumPlacementAdminView,
} from '../services/premiumPlacementsService';

// FEAT-065 admin surface (MVP-18.7).

const PremiumPlacementsPage: React.FC = () => {
  const [rows, setRows] = useState<PremiumPlacementAdminView[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await premiumPlacementsAdminService.listActive();
      setRows(data);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'We had trouble loading premium placements.',
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" sx={{ mb: 2 }}>
        Premium Placements
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Active premium area placements by zip code and caregiver.
      </Typography>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress data-testid="premium-placements-loading" />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {!loading && !error && rows.length === 0 && (
        <Alert severity="info" data-testid="premium-placements-empty">
          No active premium placements.
        </Alert>
      )}

      {!loading && rows.length > 0 && (
        <TableContainer component={Paper} data-testid="premium-placements-table">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Caregiver</TableCell>
                <TableCell>Zip</TableCell>
                <TableCell>Region</TableCell>
                <TableCell>Expires</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>{row.caregiverName}</TableCell>
                  <TableCell>{row.zip}</TableCell>
                  <TableCell>{row.regionName ?? '—'}</TableCell>
                  <TableCell>{new Date(row.expiresAt).toLocaleDateString()}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Container>
  );
};

export default PremiumPlacementsPage;
